import React, { createContext, useContext, useEffect, useState } from 'react';
import { allServices as staticServices, serviceCategories as staticCategories } from '../data/servicesData';

const ServiceCatalogContext = createContext();

export const useServiceCatalog = () => useContext(ServiceCatalogContext);

const staticById = Object.fromEntries(staticServices.map((s) => [s.id, s]));

// The admin "Edit Services" tab is the live source of truth for name, category,
// price and active/inactive state. But a service only has real image/description/
// questionnaire data once someone has actually edited it there - until then,
// fall back to the original built-in catalog for those specific fields so
// nothing on the live site goes blank the moment this ships.
function mergeWithFallback(liveService) {
  const fallback = staticById[liveService.id];
  const hasLiveQuestionnaire = Array.isArray(liveService.questionnaire) && liveService.questionnaire.length > 0;
  return {
    ...fallback,
    ...liveService,
    image: liveService.image || fallback?.image || null,
    description: liveService.description || fallback?.description || '',
    path: liveService.path || fallback?.path,
    questionnaire: hasLiveQuestionnaire ? liveService.questionnaire : null,
    // Used by Questionnaire.js only when `questionnaire` above is null.
    requiresQuestionnaire: fallback?.requiresQuestionnaire,
  };
}

export const ServiceCatalogProvider = ({ children }) => {
  const [allServices, setAllServices] = useState(staticServices);
  const [serviceCategories, setServiceCategories] = useState(staticCategories);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/services/catalog`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.services) && data.services.length > 0) {
          setAllServices(data.services.map(mergeWithFallback));
          if (Array.isArray(data.categories) && data.categories.length > 0) {
            setServiceCategories(data.categories);
          }
        }
        // If the catalog is empty or the request fails, the static fallback
        // set at initial state above is left in place untouched.
      })
      .catch((err) => console.error('Failed to load live service catalog, using built-in fallback:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <ServiceCatalogContext.Provider value={{ allServices, serviceCategories, loading }}>
      {children}
    </ServiceCatalogContext.Provider>
  );
};
